import { useMemo, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import {
  Box,
  Card,
  CardContent,
  Chip,
  Icon,
  StaticNumericSpan,
  StaticPercentSpan,
  Typography,
} from '@myn/mui';

import { KpisValuesType, Version } from '../../../types/versions.js';
import { KpisLabels } from '../constants/index.jsx';

const ZERO_VALUE = '0.00';

type IVersionKpiProps = {
  item: Version;
};

const VersionKpi: FC<IVersionKpiProps> = ({ item }) => {
  const { t } = useTranslation('@myn/planning');

  const versionKpis = useMemo(
    () =>
      Object.entries(item.channelMixVersionData?.kpis || []).reduce<
        {
          label: keyof typeof KpisLabels;
          value: KpisValuesType;
        }[]
      >((acc, item) => {
        const kpiName = item[0] as keyof typeof KpisLabels;
        const { current } = item[1];
        const isEmptyValue =
          !current ||
          current === 0 ||
          current === '0.00' ||
          current === '0.00%';

        if (isEmptyValue) {
          return acc;
        }

        return [
          ...acc,
          {
            label: KpisLabels[kpiName] as keyof typeof KpisLabels,
            value: item[1] as KpisValuesType,
          },
        ];
      }, []),
    [item.channelMixVersionData?.kpis],
  );

  const hasKpis = useMemo(
    () =>
      versionKpis.find((kpi) =>
        Number(kpi.value.current?.toString().replace('%', '')),
      ),
    [versionKpis],
  );

  const parseKpiValue = (value?: number | string | null) => {
    if (!value) {
      return ZERO_VALUE;
    }

    if (typeof value === 'string') {
      return value.replace('%', '');
    }

    return value;
  };

  const renderKpiValue = (label?: string, value?: number | string | null) => {
    if (!label || typeof value === 'object') {
      return null;
    }

    const parsedValue = parseKpiValue(value);

    switch (label) {
      case 'CTR':
      case 'VTR':
      case 'CR':
        return (
          <>
            {`${label}: `}
            <StaticPercentSpan
              number={parsedValue}
              options={{ minDecimals: 2 }}
            />
          </>
        );
      default:
        return (
          <>
            {`${label}: `}
            <StaticNumericSpan number={parsedValue} />
          </>
        );
    }
  };

  return (
    <Card>
      <CardContent sx={{ '&, :last-child': { padding: 1.5 } }}>
        <Typography variant="h6" sx={{ marginBottom: 3 }}>
          {t('KPIs')}
        </Typography>
        {hasKpis ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {versionKpis.map((kpi) => (
              <Chip
                key={kpi.label}
                size="medium"
                icon={
                  kpi.value.difference ? (
                    <Icon
                      component={
                        kpi.value.difference < 0 ? 'ArrowDown' : 'ArrowUp'
                      }
                    />
                  ) : undefined
                }
                label={renderKpiValue(kpi.label, kpi.value.current)}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 48,
            }}
          >
            <Typography variant="bodyShort01" color="text.secondary">
              {t('This version has no KPIs yet.')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VersionKpi;
